import { Button, Empty, Segmented, Space, Spin, Tooltip, Typography } from 'antd';
import {
	DownloadOutlined,
	LeftOutlined,
	RetweetOutlined,
	RightOutlined,
	RotateRightOutlined,
	ZoomInOutlined,
	ZoomOutOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

import {
	CertProposal,
	useGetCertProposalPdfQuery,
} from '../../redux/api/material-certs';

const { Text } = Typography;

// Shop's nginx (and the ingress in front of it) has served the hashed
// pdf.worker.min-*.mjs as application/octet-stream. Chrome will not run that
// as a module, getDocument never resolves, and the pane stays blank even
// though certificate-pdf/ returned a valid PDF. Re-wrap the worker bytes in a
// blob with a JS MIME and hand pdf.js a Worker — blob: URLs do not go through
// nginx, and { type: 'module' } is required because the worker is ESM (a blob
// URL has no .mjs suffix, so pdf.js would otherwise start a classic worker).
let pdfWorkerReady: Promise<void> | null = null;

const ensurePdfWorker = (): Promise<void> => {
	if (pdfjs.GlobalWorkerOptions.workerPort) {
		return Promise.resolve();
	}
	if (!pdfWorkerReady) {
		const src = new URL(
			'pdfjs-dist/build/pdf.worker.min.mjs',
			import.meta.url
		).toString();
		pdfWorkerReady = fetch(src)
			.then((response) => {
				if (!response.ok) {
					throw new Error(`pdf worker ${response.status}`);
				}
				return response.arrayBuffer();
			})
			.then((buffer) => {
				const url = URL.createObjectURL(
					new Blob([buffer], { type: 'text/javascript' })
				);
				pdfjs.GlobalWorkerOptions.workerPort = new Worker(url, {
					type: 'module',
				});
			})
			.catch((error) => {
				pdfWorkerReady = null;
				throw error;
			});
	}
	return pdfWorkerReady;
};

type PdfKind = 'certificate' | 'packing-slip';

const LABELS: Record<PdfKind, string> = {
	certificate: 'Mill certificate',
	'packing-slip': 'Packing slip',
};

// Scans are 1-bit line art, so rendering above the display's own resolution
// buys nothing but memory.
const MAX_PIXEL_RATIO = 2;

/** Save the fetched blob under a name that means something out of context. */
const saveBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename;
	anchor.click();
	URL.revokeObjectURL(url);
};

function PdfFrame({
	id,
	kind,
	initialRotation,
	downloadName,
}: {
	id: string;
	kind: PdfKind;
	initialRotation: number;
	downloadName: string;
}) {
	const { data, isFetching, isError } = useGetCertProposalPdfQuery({ id, kind });
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const taskRef = useRef<RenderTask | null>(null);

	const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
	const [parseError, setParseError] = useState(false);
	const [page, setPage] = useState(1);
	const [rotation, setRotation] = useState(initialRotation);
	const [zoom, setZoom] = useState(1);
	const [width, setWidth] = useState(0);

	// A new document means a new detected angle and page one again.
	useEffect(() => {
		setRotation(initialRotation);
		setPage(1);
		setZoom(1);
	}, [id, kind, initialRotation]);

	useEffect(() => {
		if (!data) {
			setDoc(null);
			setParseError(false);
			return;
		}
		let cancelled = false;
		let task: pdfjs.PDFDocumentLoadingTask | null = null;
		setParseError(false);
		ensurePdfWorker()
			.then(() => data.arrayBuffer())
			.then((buffer) => {
				if (cancelled) return null;
				task = pdfjs.getDocument({ data: new Uint8Array(buffer) });
				return task.promise;
			})
			.then((next) => {
				if (!cancelled && next) setDoc(next);
			})
			.catch(() => {
				if (!cancelled) {
					setDoc(null);
					setParseError(true);
				}
			});
		return () => {
			cancelled = true;
			// Destroying the loading task tears down the document and its worker.
			void task?.destroy();
		};
	}, [data]);

	// Fit-to-width has to react to the pane resizing, not just to mounting.
	useEffect(() => {
		const element = scrollRef.current;
		if (!element) return;
		const observer = new ResizeObserver((entries) => {
			setWidth(entries[0].contentRect.width);
		});
		observer.observe(element);
		setWidth(element.clientWidth);
		return () => observer.disconnect();
	}, [doc]);

	useEffect(() => {
		if (!doc || !width) return;
		let cancelled = false;

		const draw = async () => {
			const pdfPage = await doc.getPage(Math.min(page, doc.numPages));
			if (cancelled) return;
			const canvas = canvasRef.current;
			if (!canvas) return;

			const base = pdfPage.getViewport({ scale: 1, rotation });
			// 16px keeps the page clear of the scrollbar at fit width.
			const cssScale = ((width - 16) / base.width) * zoom;
			const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
			// Render at device resolution, display at CSS size, so the 1-bit
			// scans stay legible on a high-DPI screen.
			const viewport = pdfPage.getViewport({ scale: cssScale * ratio, rotation });

			canvas.width = Math.floor(viewport.width);
			canvas.height = Math.floor(viewport.height);
			canvas.style.width = `${Math.floor(viewport.width / ratio)}px`;
			canvas.style.height = `${Math.floor(viewport.height / ratio)}px`;

			const context = canvas.getContext('2d');
			if (!context) return;

			// Two render passes on one canvas throw, and resizing fires often.
			// pdfjs-dist 4.x (Node 18 / CI) takes a 2D context, not the canvas
			// handle that 5+ added.
			taskRef.current?.cancel();
			const task = pdfPage.render({ canvasContext: context, viewport });
			taskRef.current = task;
			try {
				await task.promise;
			} catch {
				// Cancelled by the next pass; nothing to report.
			}
		};

		void draw();
		return () => {
			cancelled = true;
		};
	}, [doc, page, rotation, zoom, width]);

	const pages = doc?.numPages ?? 0;

	if (isFetching) {
		return (
			<div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
				<Spin />
			</div>
		);
	}
	if (isError || !data) {
		return <Empty description="This PDF is not on file." />;
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 6 }}>
			<Flexbar>
				<Space size={4}>
					<Button
						size="small"
						icon={<LeftOutlined />}
						disabled={page <= 1}
						onClick={() => setPage((current) => Math.max(1, current - 1))}
					/>
					<Text type="secondary" style={{ fontSize: 12, minWidth: 44, textAlign: 'center' }}>
						{pages ? `${page} / ${pages}` : '—'}
					</Text>
					<Button
						size="small"
						icon={<RightOutlined />}
						disabled={!pages || page >= pages}
						onClick={() => setPage((current) => Math.min(pages, current + 1))}
					/>
				</Space>
				<Space size={4}>
					<Button
						size="small"
						icon={<ZoomOutOutlined />}
						disabled={zoom <= 0.5}
						onClick={() => setZoom((current) => Math.max(0.5, current - 0.25))}
					/>
					<Button
						size="small"
						onClick={() => setZoom(1)}
						disabled={zoom === 1}
						style={{ fontSize: 12 }}
					>
						Fit
					</Button>
					<Button
						size="small"
						icon={<ZoomInOutlined />}
						disabled={zoom >= 3}
						onClick={() => setZoom((current) => Math.min(3, current + 0.25))}
					/>
					<Tooltip title="Rotate 90 degrees">
						<Button
							size="small"
							icon={<RotateRightOutlined />}
							onClick={() => setRotation((current) => (current + 90) % 360)}
						/>
					</Tooltip>
					{/* Auto-detection cannot tell 0 from 180 -- an upside-down page
					    ripples identically to an upright one -- and roughly one
					    scan in forty arrives flipped, so it is a button. */}
					<Tooltip title="Flip 180 degrees, for a page scanned upside down">
						<Button
							size="small"
							icon={<RetweetOutlined />}
							onClick={() => setRotation((current) => (current + 180) % 360)}
						/>
					</Tooltip>
					{/* The way out when this screen cannot link the heat: save the
					    certificate and attach it by hand in the materials module. */}
					<Tooltip title="Download this PDF">
						<Button
							size="small"
							icon={<DownloadOutlined />}
							onClick={() => saveBlob(data, downloadName)}
						/>
					</Tooltip>
				</Space>
			</Flexbar>
			<div
				ref={scrollRef}
				style={{
					flex: 1,
					minHeight: 0,
					overflow: 'auto',
					background: '#f5f5f5',
					textAlign: 'center',
				}}
			>
				{parseError ? (
					<div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
						<Empty description="This PDF could not be rendered." />
					</div>
				) : !doc ? (
					<div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
						<Spin />
					</div>
				) : (
					<canvas
						ref={canvasRef}
						style={{ display: 'inline-block', margin: '0 auto' }}
					/>
				)}
			</div>
		</div>
	);
}

function Flexbar({ children }: { children: React.ReactNode }) {
	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				gap: 8,
			}}
		>
			{children}
		</div>
	);
}

/**
 * The two documents behind a proposal, alongside the decision.
 *
 * Rendered here rather than handed to the browser's PDF plugin: the
 * certificates are scans that often arrive sideways, and only our own canvas
 * can be turned upright, fitted to the pane, and paged through without the
 * plugin's thumbnail rail eating a third of the width.
 */
export default function PdfPane({ proposal }: { proposal: CertProposal | null }) {
	const available = useMemo<PdfKind[]>(() => {
		if (!proposal) return [];
		const kinds: PdfKind[] = [];
		if (proposal.has_certificate_pdf) kinds.push('certificate');
		if (proposal.has_packing_slip_pdf) kinds.push('packing-slip');
		return kinds;
	}, [proposal]);

	const [kind, setKind] = useState<PdfKind | null>(null);
	const active = kind && available.includes(kind) ? kind : available[0] ?? null;

	// Only the certificate is a scan worth turning; the slip arrives upright.
	const rotationFor = useCallback(
		(which: PdfKind) =>
			which === 'certificate' ? proposal?.certificate_rotation ?? 0 : 0,
		[proposal]
	);

	/**
	 * Named from the heat, not from Alro's `681935_009692_RVPRTPF_...PDF`.
	 * A downloaded certificate is on its way to being attached by hand in the
	 * materials module, where the heat is the only thing that identifies it.
	 */
	const nameFor = useCallback(
		(which: PdfKind) => {
			const safe = (value: string) =>
				(value || 'unknown').replace(/[^A-Za-z0-9._-]+/g, '-');
			return which === 'certificate'
				? proposal?.heat
					? `HT_${safe(proposal.heat)}_cert.pdf`
					: `PO_${safe(proposal?.po ?? '')}_${
							proposal?.line_number ? `line_${proposal.line_number}_` : ''
						}cert.pdf`
				: `PO_${safe(proposal?.po ?? '')}_packing_slip.pdf`;
		},
		[proposal]
	);

	if (!proposal) {
		return <Empty description="Pick a heat to read its paperwork." />;
	}
	if (!active) {
		return <Empty description="No PDF has arrived for this heat yet." />;
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 6 }}>
			{available.length > 1 && (
				<Segmented
					size="small"
					value={active}
					onChange={(value) => setKind(value as PdfKind)}
					options={available.map((option) => ({
						value: option,
						label: LABELS[option],
					}))}
				/>
			)}
			<div style={{ flex: 1, minHeight: 0 }}>
				<PdfFrame
					key={`${proposal.id}-${active}`}
					id={proposal.id}
					kind={active}
					initialRotation={rotationFor(active)}
					downloadName={nameFor(active)}
				/>
			</div>
		</div>
	);
}
